<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                <v-icon class="me-2">mdi-filter</v-icon>
                Employee Salary Details Filter
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="handleFilterSubmit">
                  <v-row class="py-2" dense>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="filters.department_id"
                        :items="departments"
                        :item-title="(item) => item.name || item.department_name || ''"
                        item-value="id"
                        label="Department"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                        :loading="departmentsLoading"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-autocomplete
                        v-model="filters.employee_id"
                        :items="employees"
                        :item-title="(item) => `${item.name} - ${item.code || item.employee_code || ''}`"
                        item-value="id"
                        label="Employee"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                        :loading="employeesLoading"
                        placeholder="Select employee"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="filters.year"
                        label="Year"
                        variant="outlined"
                        density="compact"
                        type="number"
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="filters.month"
                        :items="monthOptions"
                        item-title="label"
                        item-value="value"
                        label="Month"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="filters.from_date"
                        label="From Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        hide-details="auto"
                        clearable
                        :max="filters.to_date"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="filters.to_date"
                        label="To Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        hide-details="auto"
                        clearable
                        :min="filters.from_date"
                      />
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-end">
                      <v-btn
                      type="submit"
                      color="primary"
                      variant="flat"
                      :loading="listLoading"
                      prepend-icon="mdi-magnify"
                      density="compact"
                      height="40"
                    >
                      Search
                    </v-btn>

                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-row class="mt-2">
            <v-col cols="12">
              <div class="text-h6 mb-3">
                <v-icon class="me-2">mdi-format-list-bulleted</v-icon>
                Employee Salary Details List
              </div>
              
              <v-row class="py-2">
                <v-col md="4" cols="12">
                  <v-text-field
                    v-model="search"
                    append-inner-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    class="pl-2"
                    clearable
                  ></v-text-field>
                </v-col>

                <v-col md="5" cols="12">
                  <div class="d-flex align-center flex-column">
                    <v-btn-group
                      v-if="employeeSalaryList.length > 0"
                      variant="outlined"
                      density="compact"
                      size="x-small"
                      divided
                    >
                      <v-btn
                        prepend-icon="mdi mdi-download"
                        variant="flat"
                        color="success"
                        @click="exportToCSV"
                        :disabled="!employeeSalaryList.length"
                      >
                        CSV
                      </v-btn>
                      <v-btn
                        prepend-icon="mdi mdi-download"
                        variant="flat"
                        color="primary"
                        @click="exportToExcel"
                        :disabled="!employeeSalaryList.length"
                      >
                        EXCEL
                      </v-btn>
                      <v-btn
                        prepend-icon="mdi mdi-download"
                        variant="flat"
                        color="red"
                        @click="exportToPDF"
                        :disabled="!employeeSalaryList.length"
                      >
                        PDF
                      </v-btn>
                    </v-btn-group>
                  </div>
                </v-col>

                <v-col md="3" cols="12"></v-col>
              </v-row>

              <v-progress-linear
                v-if="listLoading"
                indeterminate
                color="primary"
                class="mb-4"
              />

              <v-data-table
                :headers="tableHeaders"
                :items="filteredEmployeeSalaryList"
                :loading="listLoading"
                :search="search"
                :items-per-page="itemsPerPage"
                density="compact"
                class="elevation-1"
              >
                <template #item.employee_code="{ item }">
                  {{ item.employee?.code || '—' }}
                </template>
                <template #item.employee_name="{ item }">
                  {{ item.employee?.name || '—' }}
                </template>
                <template #item.department="{ item }">
                  {{ item.department?.name || '—' }}
                </template>
                <template #item.designation="{ item }">
                  {{ item.designation?.name || '—' }}
                </template>
                <template #item.process_code="{ item }">
                  {{ item.master?.code || item.salary_process_master?.code || '—' }}
                </template>
                <template #item.year_month="{ item }">
                  {{ item.year }} / {{ getMonthName(item.month) }}
                </template>
                <template #item.gross_payable="{ item }">
                  {{ formatNumber(item.gross_payable) }}
                </template>
                <template #item.total_deduction="{ item }">
                  {{ formatNumber(item.total_deduction) }}
                </template>
                <template #item.total_net_payable="{ item }">
                  {{ formatNumber(item.total_net_payable) }}
                </template>
                <template #item.actions="{ item }">
                  <v-btn
                    size="small"
                    variant="tonal"
                    color="primary"
                    @click="viewDetails(item.id)"
                  >
                    PaySlip
                  </v-btn>
                </template>
                <template #no-data>
                  <div class="py-6 text-center text-medium-emphasis">
                    No employee salary details found.
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, onMounted, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'


const router = useRouter()
const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext
const panel = ref([0])
const departmentsLoading = ref(false)
const employeesLoading = ref(false)
const listLoading = ref(false)
const itemsPerPage = ref(10)
const search = ref('')
const employeeSalaryList = ref([])
const departments = ref([])
const employees = ref([])

const filters = reactive({
  department_id: null,
  employee_id: null,
  year: '',
  month: null,
  from_date: null,
  to_date: null
})

const monthOptions = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
]

const tableHeaders = [
  { title: '#SL', key: 'index', sortable: false, width: '60px', align: 'center' },
  { title: 'Employee Code', key: 'employee_code', sortable: false },
  { title: 'Employee Name', key: 'employee_name', sortable: false },
  { title: 'Department', key: 'department', sortable: false },
  { title: 'Designation', key: 'designation', sortable: false },
  { title: 'Process Code', key: 'process_code', sortable: false },
  { title: 'Year / Month', key: 'year_month', sortable: false },
  { title: 'Gross Payable', key: 'gross_payable', sortable: false, align: 'end' },
  { title: 'Total Deduction', key: 'total_deduction', sortable: false, align: 'end' },
  { title: 'Total Net Payable', key: 'total_net_payable', sortable: false, align: 'end' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '100px' }
]

const filteredEmployeeSalaryList = computed(() => {
  let list = employeeSalaryList.value.map((item, index) => ({
    ...item,
    index: index + 1
  }))

  if (search.value) {
    const searchLower = search.value.toLowerCase()
    list = list.filter(item => {
      return (
        (item.employee?.code || '').toLowerCase().includes(searchLower) ||
        (item.employee?.name || '').toLowerCase().includes(searchLower) ||
        (item.department?.name || '').toLowerCase().includes(searchLower) ||
        (item.designation?.name || '').toLowerCase().includes(searchLower) ||
        (item.master?.code || item.salary_process_master?.code || '').toLowerCase().includes(searchLower)
      )
    })
  }

  return list
})

const formatNumber = (value, fraction = 2) => {
  if (value === undefined || value === null || value === '' || isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const getMonthName = (monthNumber) => {
  if (!monthNumber) return '—'
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return monthNames[monthNumber - 1] || monthNumber
}

const loadDepartments = async () => {
  departmentsLoading.value = true
  try {
    const response = await store.dispatch('getDepartments')
    if (response.data.success && response.data.data) {
      departments.value = response.data.data
    } else if (response.data.data) {
      departments.value = response.data.data
    } else {
      departments.value = []
    }
  } catch (error) {
    departments.value = []
    toast.error(error.message || 'Failed to load departments')
  } finally {
    departmentsLoading.value = false
  }
}

const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    if (response.data.success && response.data.data) {
      employees.value = response.data.data
    } else if (response.data.data) {
      employees.value = response.data.data
    } else {
      employees.value = []
    }
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const fetchEmployeeSalaryList = async () => {
  listLoading.value = true
  try {
    const params = {}
    if (filters.department_id) params.department_id = filters.department_id
    if (filters.employee_id) params.employee_id = filters.employee_id
    if (filters.year) params.year = filters.year
    if (filters.month) params.month = filters.month
    if (filters.from_date) params.from_date = formatDateDash(filters.from_date)
    if (filters.to_date) params.to_date = formatDateDash(filters.to_date)

    const response = await store.dispatch('getEmployeeSalaryList', params)
    if (response.success) {
      employeeSalaryList.value = response.data || []
    } else {
      toast.error(response.message || 'Failed to load employee salary list')
      employeeSalaryList.value = []
    }
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || 'Failed to load employee salary list')
    employeeSalaryList.value = []
  } finally {
    listLoading.value = false
  }
}

const handleFilterSubmit = () => {
  fetchEmployeeSalaryList()
}

const resetFilters = () => {
  filters.department_id = null
  filters.employee_id = null
  filters.year = ''
  filters.month = null
  filters.from_date = null
  filters.to_date = null
  fetchEmployeeSalaryList()
}

const viewDetails = (id) => {
  if (!id) {
    toast.error('Employee Salary Detail ID is missing')
    return
  }
  router.push({
    name: 'employee-salary-details',
    params: { id: id }
  })
}

// Export functions
const exportToPDF = () => {
  if (!employeeSalaryList.value.length) {
    toast.warning('No data to export')
    return
  }

  const columns = [
    '#SL',
    'Employee Code',
    'Employee Name',
    'Department',
    'Designation',
    'Process Code',
    'Year / Month',
    'Gross Payable',
    'Total Deduction',
    'Total Net Payable'
  ]

  const rows = filteredEmployeeSalaryList.value.map((item) => [
    item.index,
    item.employee?.code || '—',
    item.employee?.name || '—',
    item.department?.name || '—',
    item.designation?.name || '—',
    item.salary_process_master?.code || item.master?.code || '—',
    `${item.year} / ${getMonthName(item.month)}`,
    formatNumber(item.gross_payable),
    formatNumber(item.total_deduction),
    formatNumber(item.total_net_payable)
  ])

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToPDF && proxy?.$fileDate) {
    proxy.$exportTableToPDF(
      columns,
      rows,
      'Employee Salary Details List',
      'employee_salary_details_list_' + proxy.$fileDate()
    )
  }
}

const exportToExcel = () => {
  if (!employeeSalaryList.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [[
    '#SL',
    'Employee Code',
    'Employee Name',
    'Department',
    'Designation',
    'Process Code',
    'Year / Month',
    'Gross Payable',
    'Total Deduction',
    'Total Net Payable'
  ]]

  filteredEmployeeSalaryList.value.forEach((item) => {
    data.push([
      item.index,
      item.employee?.code || '—',
      item.employee?.name || '—',
      item.department?.name || '—',
      item.designation?.name || '—',
      item.master?.code || item.salary_process_master?.code || '—',
      `${item.year} / ${getMonthName(item.month)}`,
      formatNumber(item.gross_payable),
      formatNumber(item.total_deduction),
      formatNumber(item.total_net_payable)
    ])
  })

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'employee_salary_details_list_' + proxy.$fileDate())
  }
}

const exportToCSV = () => {
  if (!employeeSalaryList.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [[
    '#SL',
    'Employee Code',
    'Employee Name',
    'Department',
    'Designation',
    'Process Code',
    'Year / Month',
    'Gross Payable',
    'Total Deduction',
    'Total Net Payable'
  ]]

  filteredEmployeeSalaryList.value.forEach((item) => {
    data.push([
      item.index,
      item.employee?.code || '—',
      item.employee?.name || '—',
      item.department?.name || '—',
      item.designation?.name || '—',
      item.master?.code || item.salary_process_master?.code || '—',
      `${item.year} / ${getMonthName(item.month)}`,
      formatNumber(item.gross_payable),
      formatNumber(item.total_deduction),
      formatNumber(item.total_net_payable)
    ])
  })

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
    proxy.$exportTableToCSV(data, 'employee_salary_details_list_' + proxy.$fileDate())
  }
}

onMounted(() => {
  loadDepartments()
  loadEmployees()
  // fetchEmployeeSalaryList()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

